import { Card, CardBody, Stacked } from '@statsbomb/kitbag-components';

import { FilterScopeProvider, ResetAllFiltersButton } from '@statsbomb/kitbag-global-football';
import { MetricValueMode } from '@statsbomb/parachute-types';
import { useAtomValue } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { ScopeProvider } from 'jotai-scope';
import { useTranslation } from 'react-i18next';
import { unwrappedTeamGameDataDefaultColumnsAtom } from '@/atoms/dataLocker/team/game/table';
import { tableColumnEntitiesAtom } from '@/atoms/queries/tableColumns';
import { teamGameStatsFilterParamsAtom } from '@/atoms/queries/team/teamAggData';
import { teamGameAggsCountAtom } from '@/atoms/queries/team/teamGameAggData';
import { metricValueModeAtom } from '@/atoms/table';
import { unwrappedSelectedTeamNameAtom } from '@/atoms/team/info';
import { teamIdAtom } from '@/atoms/team/team';
import { TableDataControls } from '@/components/controls/TableDataControls';
import { PageTitle } from '@/components/head/PageTitle';
import { LayoutWithFilters } from '@/components/layout/LayoutWithFilters';
import { ScopedTableWithDefaultColumns } from '@/components/tables/ScopedTableWithDefaultColumns';
import { TablePagination } from '@/components/tables/TablePagination';
import { TeamHighLevelFilters } from '@/components/team/filters/TeamHighLevelFilters';
import { TeamGameAggsTable } from '@/components/team/tables/TeamGameAggsTable';
import { useCsvDownload } from '@/hooks/exporting/useCsvDownload';
import { useEntityPageTitle } from '@/hooks/useEntityPageTitle';
import { useTableColumns } from '@/hooks/useTableColumns';
import { teamGamesTotalAggregatesUrl } from '@/query/url';
import { TableId } from '@/types/table';

const CsvDownload = ({ pageTitle }: { pageTitle: string }) => {
  const teamId = useAtomValue(teamIdAtom);
  const teamGameStatsFilterParams = useAtomValue(teamGameStatsFilterParamsAtom);
  useCsvDownload(pageTitle, teamGamesTotalAggregatesUrl(teamId, teamGameStatsFilterParams));
  return null;
};

const HydrateTeamGamesAtoms = ({ children }: { children: React.ReactNode }) => {
  useHydrateAtoms([[metricValueModeAtom, MetricValueMode.Total]]);
  useTableColumns(['competition', 'game', 'team_total_metric']);
  return children;
};

export const TeamGamesPage = () => {
  const { t } = useTranslation(['navigation', 'games', 'general']);

  const teamName = useAtomValue(unwrappedSelectedTeamNameAtom);
  const pageTitle = useEntityPageTitle(t('secondary.games', { ns: 'navigation' }), teamName);

  return (
    <ScopeProvider atoms={[tableColumnEntitiesAtom, metricValueModeAtom]}>
      <HydrateTeamGamesAtoms>
        <PageTitle>{pageTitle}</PageTitle>
        <FilterScopeProvider scope="main">
          <LayoutWithFilters
            filters={<TeamHighLevelFilters />}
            filtersTitle={t('filters.other', { ns: 'general' })}
            filtersActions={<ResetAllFiltersButton />}
          >
            <ScopedTableWithDefaultColumns
              defaultColumnsAtom={unwrappedTeamGameDataDefaultColumnsAtom}
              tableId={TableId.TEAM_GAMES}
            >
              <CsvDownload pageTitle={pageTitle} />
              <Card>
                <TableDataControls heading={t('matchStats', { ns: 'games' })} />
                <CardBody>
                  <Stacked direction="column" gap="medium">
                    <TeamGameAggsTable />
                    <TablePagination dataLengthAtom={teamGameAggsCountAtom} />
                  </Stacked>
                </CardBody>
              </Card>
            </ScopedTableWithDefaultColumns>
          </LayoutWithFilters>
        </FilterScopeProvider>
      </HydrateTeamGamesAtoms>
    </ScopeProvider>
  );
};
